import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import LegalDocumentDialog from "./components/legal/LegalDocumentDialog";
import { LegalDocType } from "./legal/legalDocuments";

function useIsSmallScreen(breakpointPx = 768) {
  const get = () =>
    typeof window !== "undefined"
      ? window.matchMedia(`(max-width:${breakpointPx}px)`).matches
      : false;
  const [small, setSmall] = useState<boolean>(get());
  useEffect(() => {
    const mql = window.matchMedia(`(max-width:${breakpointPx}px)`);
    const onChange = (e: MediaQueryListEvent | MediaQueryList) =>
      setSmall("matches" in e ? e.matches : (e as MediaQueryList).matches);
    setSmall(mql.matches);
    if (mql.addEventListener) mql.addEventListener("change", onChange as any);
    else mql.addListener(onChange as any);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange as any);
      else mql.removeListener(onChange as any);
    };
  }, [breakpointPx]);
  return small;
}

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isSmall = useIsSmallScreen(768);
  const isMobileRoute = location.pathname.startsWith("/m");
  const shouldUseMobile = isMobileRoute && isSmall;
  const [legalOpen, setLegalOpen] = useState(false);
  const [legalType, setLegalType] = useState<LegalDocType>("terms");

  const go = (desktopPath: string, mobilePath: string) => {
    navigate(shouldUseMobile ? mobilePath : desktopPath);
  };
  const openLegal = (type: LegalDocType) => {
    setLegalType(type);
    setLegalOpen(true);
  };

  return (
    <>
      <LegalDocumentDialog open={legalOpen} onClose={() => setLegalOpen(false)} type={legalType} />
      <footer className="pi-footer">
        <div className="pi-footer__inner">
          <div className="pi-footer__brand-row">
            <div className="pi-footer__brand">PolicyIntel</div>
            <div className="pi-footer__tagline">Signal over Noise · 판단보다 많은 정보를 보여주지 않습니다.</div>
          </div>

          <div className="pi-footer__notice-grid">
            <section>
              <h2 className="pi-footer__section-title">서비스 이용 유의사항</h2>
              <div className="pi-footer__copy">
                <div>• 본 서비스는 공개된 정책·산업·공시·시장 데이터를 수집·정리·시각화한 <strong>온라인 정보 제공 서비스</strong>입니다.</div>
                <div>• 서비스 내 제공되는 분석, 점수, 분류, 요약, 랭킹, 시각화 및 기타 모든 정보는 <strong>일반적 참고자료</strong>일 뿐이며, <strong>특정 종목에 대한 투자 권유, 투자자문, 매수·매도 추천 또는 수익 보장을 의미하지 않습니다.</strong></div>
                <div>• 회사는 이용자의 투자목적, 재무상황, 위험선호도 등 개별 사정을 고려한 <strong>맞춤형 자문을 제공하지 않습니다.</strong></div>
                <div>• 서비스 내 정보는 시스템 알고리즘 및 외부 데이터에 기반하여 자동 또는 반자동으로 생성될 수 있으며, <strong>정보의 정확성, 완전성, 최신성, 적시성 및 특정 목적 적합성을 보장하지 않습니다.</strong></div>
                <div>• 중요한 판단 전에는 공시, 원문 기사, 정책 자료, 기업 공시, 차트 및 기타 <strong>원자료를 이용자가 직접 추가 확인</strong>해야 합니다.</div>
                <div>• 서비스 이용 과정에서 제공되는 수치, 반응, 점수, 연결 정보 및 기타 표현은 <strong>참고용 정보의 정리 결과</strong>이며, 실제 시장 상황이나 개별 종목의 미래 결과를 보장하지 않습니다.</div>
                <div>• <strong>모든 투자 판단과 그에 따른 책임은 이용자 본인에게 있습니다.</strong> 회사는 관련 법령상 허용되는 범위 내에서 이용자의 거래, 손익, 기대성과 또는 의사결정 결과에 대해 책임을 부담하지 않습니다.</div>
              </div>
            </section>

            <section>
              <h2 className="pi-footer__section-title">정기결제 및 해지 안내</h2>
              <div className="pi-footer__copy">
                <div>• 유료서비스는 <strong>월 구독형 정기결제 방식</strong>으로 제공될 수 있으며, 결제 주기와 금액은 결제 화면 또는 별도 안내 페이지에 표시됩니다.</div>
                <div>• 정기결제는 이용자가 사전에 동의한 주기에 따라 자동 갱신될 수 있습니다.</div>
                <div>• 이용자는 서비스 내 계정 설정, 결제 관리 화면 또는 고객문의 절차를 통해 <strong>다음 결제 예정일 이전까지 구독을 해지</strong>할 수 있으며, 해지 완료 시 다음 결제일부터 추가 청구가 이루어지지 않습니다.</div>
                <div>• 이미 결제가 완료되어 이용이 개시된 당월 이용기간에 대해서는, 관계 법령 및 별도 정책이 허용하는 경우를 제외하고 환급이 제한될 수 있습니다.</div>
                <div>• 다만, 결제 오류, 중복결제 또는 회사의 중대한 귀책사유로 정상적인 서비스 제공이 어려운 경우에는 관련 법령과 정책에 따라 취소·환급 또는 보상 절차가 진행될 수 있습니다.</div>
              </div>
            </section>

            <section>
              <h2 className="pi-footer__section-title">데이터 이용 및 저작권 안내</h2>
              <div className="pi-footer__copy">
                <div>• 본 서비스는 공개된 보도자료, 공시, 정책 자료, 통계 및 시장 데이터를 수집·정리하여 요약, 분류, 분석 및 시각화 형태로 제공합니다.</div>
                <div>• 원문, 기사, 이미지, 표, 데이터베이스 및 기타 원저작물에 관한 권리는 각 권리자에게 있습니다.</div>
                <div>• 당사는 <strong>관련 법령에 따른 정당한 범위 내에서</strong> 출처 표시, 원문 연결, 요약 제공 또는 필요한 범위의 정보 표시를 위해 노력합니다.</div>
                <div>• 서비스에 표시되는 요약, 제목, 분류, 링크 및 데이터 매핑 정보는 <strong>원저작물 자체의 권리 이전이나 이용허락을 의미하지 않습니다.</strong></div>
                <div>• 권리자 요청이 있거나 권리 침해 우려가 확인되는 경우, 관련 게시물, 요약, 링크 또는 노출 방식에 대해 <strong>검토 후 수정, 비노출 또는 삭제 조치</strong>를 할 수 있습니다.</div>
              </div>
            </section>
          </div>

          <nav className="pi-footer__nav" aria-label="푸터 메뉴">
            <button className="pi-footer__link" type="button" onClick={() => go("/board", "/m/board")}>게시판</button>
            <button className="pi-footer__link" type="button" onClick={() => go("/faq", "/m/faq")}>FAQ</button>
            <button className="pi-footer__link" type="button" onClick={() => go("/terms", "/terms")}>이용약관</button>
            <button className="pi-footer__link" type="button" onClick={() => go("/privacy", "/privacy")}>개인정보처리방침</button>
            <button className="pi-footer__link" type="button" onClick={() => openLegal("paidService")}>유료서비스 이용약관</button>
            <button className="pi-footer__link" type="button" onClick={() => openLegal("refund")}>구독해지 및 환급정책</button>
          </nav>

          <div className="pi-footer__company">
            <p>© 2026 <strong>PolicyIntel</strong>. All rights reserved.</p>
            <p>서비스명: <strong>PolicyIntel</strong> | 상호: <strong>Policy-Intel</strong></p>
            <p>대표자: <strong>이경민</strong> | 사업자등록번호: <strong>544-37-01451</strong></p>
            <p>주소: <strong>경기도 김포시 김포한강9로 79, 4층 401-434A호</strong></p>
            <p>이메일: <strong>kento15@naver.com</strong></p>
            <p>통신판매업 신고번호: <strong>신고 전</strong></p>
            <p>※ 통신판매업 신고 완료 후 신고번호는 즉시 업데이트되어야 하며, 결제 페이지 및 사업자 정보 노출 영역과 동일하게 유지되어야 합니다.</p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
